import useSWR from "swr";
import {
  Paper,
  Skeleton,
  Grid,
  Button,
  Modal,
  Text,
  Select,
  TextInput,
  Group
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import WalletCard from "../../../../common/components/WalletCard/WalletCard";
import WalletTransaction from "../../containers/WalletTransaction/Transaction";
import AddTransactionForm from "../../forms/AddTransactionForm/AddTransactionForm";
import WalletStats from "../../components/WalletStats/WalletStats";
import {
  successNotifications,
  errorNotifications,
} from "../../../../utils/handleNotifications";
import { generateWalletGroup } from "../../../../utils/mapDataToOptions";
import { fetcher, multiFormatUploader } from "../../../../services/fetcher";
import { useState } from "react";
import classes from "./MasterWallet.module.css";

const endpoint = "asc-wallet/master_wallets/";
const addFundEndpoint = "asc-wallet/master_wallet_transactions/";
const deductFundEndpoint = "asc-wallet/master-wallet/deduct/";
const userWallets = "asc-wallet/user-wallets-list/";

const MasterWallet = () => {
  const [opened, { open, close }] = useDisclosure(false);
  const [deductModalOpened, { open: openDeductModal, close: closeDeductModal }] = useDisclosure(false);
  const [userWalletId, setUserWalletId] = useState("");
  const [deductAmount, setDeductAmount] = useState("");

 const swrConfig = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  revalidateIfStale: false,
};

const { data: wallet, isLoading, error } = useSWR(
  endpoint,
  fetcher,
  swrConfig
);

  const { data, isLoading: isDataLoading } = useSWR(
    userWallets,
    fetcher,
    swrConfig
  );

  const masterWallet = wallet?.[0] ?? null;

  const addFundMutation = async (fund) => {
    try {
      const payload = {
        ...fund,
        transaction_date: fund?.transaction_date
          ? new Date(fund.transaction_date).toISOString()
          : fund?.transaction_date,
      };

      if (!payload.screenshot) {
        delete payload.screenshot;
      }

      await multiFormatUploader(`${addFundEndpoint}`, "POST", payload);
      successNotifications("Success! Transaction");
      close();
    } catch (error) {
      console.log(error);
      errorNotifications(error.response.data, error);
    }
  };

  const deductFundMutation = async () => {
    const deductData = {
      user_wallet_id: userWalletId,
      amount: parseFloat(deductAmount),
      description: "deduct",
    };

    try {
      await fetcher(deductFundEndpoint, "POST", deductData);
      successNotifications("Funds deducted successfully!");
      closeDeductModal();
    } catch (error) {
      console.log(error);
      errorNotifications(error.response.data, error);
    }
  };

  const onSubmit = (data) => {
    addFundMutation(data);
  };

  if (error) {
    return <p>Something went wrong</p>;
  }

  return (
    <>
      <Paper p="lg" shadow="sm" radius="md" className={classes.statsPaper}>
        <Text fw={600} size="md" c="#333" className={classes.title}>
          Master Wallet Details
        </Text>
        <Grid align="stretch" gutter="md">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <WalletStats
              deposit={wallet?.[0]?.total_deposits}
              withdrawal={wallet?.[0]?.total_withdrawals}
              loading={isLoading}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            {isLoading ? (
              <Skeleton height={100} radius="md" />
            ) : masterWallet ? (
              <WalletCard
                compact
                balance={masterWallet.total_balance}
                type={masterWallet.currency_type}
              />
            ) : (
              <Text c="dimmed" size="sm">No wallet data available.</Text>
            )}
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper shadow="sm" radius="md" className={classes.transactionsPaper}>
        <WalletTransaction
          toolbarRightContent={
            <Group gap="sm" wrap="nowrap">
              <Button variant="outline" h={44} onClick={open}>
                + Add Funds
              </Button>
              <Button variant="outline" h={44} onClick={openDeductModal}>
                - Deduct Funds
              </Button>
            </Group>
          }
        />
      </Paper>

      <Modal
        opened={opened}
        onClose={close}
        size="50rem"
        centered
        title={"Wallet Details"}
        closeButtonProps={{
          icon: (
            <img
              src="/images/svg/close.svg"
              style={{ width: "1.6rem" }}
              alt="close-icon"
            />
          ),
        }}
      >
        <AddTransactionForm
          onSubmit={onSubmit}
          wallets={!isDataLoading && generateWalletGroup(data)}
          closeModal={close}
        />
      </Modal>

      {/* Deduct Fund Modal */}
      <Modal
        opened={deductModalOpened}
        onClose={closeDeductModal}
        size="30rem"
        centered
        title={"Deduct Funds"}
      >
        <Select
          label="User Wallet ID"
          placeholder="Select user wallet ID"
          data={!isDataLoading ? generateWalletGroup(data) : []}
          value={userWalletId}
          onChange={setUserWalletId}
          required
        />
        <TextInput
          label="Amount to Deduct"
          placeholder="Enter amount to deduct"
          value={deductAmount}
          onChange={(e) => setDeductAmount(e.currentTarget.value)}
          required
          type="number"
          mt="lg"
        />
        <Group justify="center">
          <Button mt="lg" onClick={deductFundMutation}>
            Deduct Amount
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default MasterWallet;
